import { Drawer } from "antd";
import { Classroom } from "lib/common/types/classroom";
import { Registrations } from "lib/common/types/registration";
import myaxios from "lib/myAxios";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useEffect } from "react";
import { host } from "utils/constants";
import { createClassroomSlug } from "utils/slugify";
import Spinner from "../atomic/Spinner";
import ContentRenderer from "../ContentRenderer";
import EsewaPayment from "../Payments/Esewa";
import KhaltiPayment from "../Payments/Khalti";
import { message as _message } from "antd";
import RegisterUploadImage from "./RegisterUploadImage";
import Image from "next/image";
import {
  ShieldCheck,
  QrCode,
  CaretDown,
  Warning,
  Clock,
} from "@phosphor-icons/react";

interface PaymentMethodsProps extends React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {
  onNext?: () => void;
  refetch: () => {};
  classRoom: Classroom;
  registration: Registrations;
  paymentDetails: any;
}

function PaymentMethods({
  children,
  onNext,
  classRoom,
  registration,
  refetch,
  paymentDetails,
}: PaymentMethodsProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    if (router.query.onboard && router.query.onboard.includes("esewasuccess")) {
      const verifyPayment = async () => {
        setIsLoading(true);

        try {
          const response = await myaxios.put(
            host + "/eu/classroom/payment/verify/esewa",
            {
              amt: router.query["amt"],
              pid: router.query["oid"],
              refId: router.query["refId"],
            },
          );

          const { success, message } = response.data.data;

          if (success) {
            _message?.success(message);
            onNext && onNext();
          } else {
            _message?.error(message);
            router.replace(
              "/classrooms/" +
                createClassroomSlug(classRoom.name, classRoom._id.$oid) +
                "?onboard=esewafailure",
            );
          }
        } catch {
          // Keep the verification screen visible on failure.
        } finally {
          setIsLoading(false);
        }
      };

      void verifyPayment();
    }
  }, [classRoom, onNext, router]);

  if (router.query.onboard && router.query.onboard.includes("esewasuccess")) {
    return (
      <div className="flex flex-col items-center text-center gap-3 py-6">
        {isLoading && <Spinner />}
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Verifying your eSewa payment — this only takes a moment.
        </p>
      </div>
    );
  }
  if (router.query.onboard && router.query.onboard.includes("esewafailure")) {
    return (
      <div className="flex flex-col items-center text-center gap-3 py-6">
        <Warning size={22} className="text-red-500" />
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          Payment was not completed.
        </p>
        <button
          type="button"
          onClick={() =>
            router.replace(
              "/classrooms/" +
                createClassroomSlug(classRoom.name, classRoom._id.$oid) +
                "?onboard=payment",
            )
          }
          className="mt-1 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary-600 text-white text-sm font-semibold transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!registration)
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
        You need to fill up the registration form first.
      </div>
    );
  if (registration && registration.isVerified)
    return (
      <div className="text-center text-sm text-gray-600 dark:text-gray-400 py-6">
        You are already verified.
        <br />
        Please proceed to next step.
      </div>
    );

  const hasEsewa = classRoom.paymentModes.includes("esewa");
  const hasKhalti = classRoom.paymentModes.includes("khalti");
  const hasGateway = hasEsewa || hasKhalti;
  const hasManual = classRoom.paymentModes.includes("generic");
  const isQADCourse = classRoom.name === "Clamphook: IOE QAD 2079";

  // `classRoom.fee` is a legacy field the backend no longer writes to on
  // edit (admin price updates only ever touch initialPrice/discountedPrice),
  // so it can go stale forever on older classrooms. Always derive the
  // amount from the same fields the classroom page displays and the
  // backend actually verifies payments against.
  const currentFee = (
    classRoom.discountedPrice ??
    classRoom.initialPrice ??
    (classRoom.fee ? Number(classRoom.fee) : 0)
  ).toString();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          Complete your enrollment
        </p>
        <h3 className="text-lg font-black text-gray-900 dark:text-gray-100 mt-0.5">
          {isQADCourse ? "Submit identification" : "Pay to confirm your seat"}
        </h3>
        {!isQADCourse && currentFee && currentFee !== "0" && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Amount due: <span className="font-bold text-gray-900 dark:text-gray-100">Rs. {currentFee}</span>
          </p>
        )}
      </div>

      {hasGateway && (
        <div className="space-y-2.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
            <ShieldCheck size={14} className="text-primary dark:text-primary-300 shrink-0" weight="fill" />
            Pay instantly — verified automatically
          </p>
          {/* Stacked, not a grid: this renders inside containers as narrow
              as the ~340px desktop sidebar, and a viewport-based `sm:`
              breakpoint would force 2 columns there regardless of the
              actual space available, truncating the gateway names. */}
          <div className="flex flex-col gap-3">
            {hasEsewa && (
              <EsewaPayment
                classroomId={classRoom._id.$oid}
                classroomName={classRoom.name}
                registrantId={registration._id.$oid}
                fee={currentFee}
              />
            )}
            {hasKhalti && (
              <KhaltiPayment
                classroomId={classRoom._id.$oid}
                onNext={onNext}
                registrantInfo={{
                  _id: { $oid: registration._id.$oid },
                  isVerified: registration.isVerified,
                }}
                fee={parseInt(currentFee)}
              />
            )}
          </div>
        </div>
      )}

      {hasManual && (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60">
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-4 text-left"
          >
            <div className="h-9 w-9 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
              <QrCode size={17} className="text-gray-600 dark:text-gray-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {isQADCourse
                  ? "Upload identification document"
                  : hasGateway
                    ? "Pay manually — QR / bank transfer"
                    : "Pay via QR / bank transfer"}
              </p>
              <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                <Clock size={11} />
                {isQADCourse
                  ? "Citizenship, Passport, or Driving Licence"
                  : "Manual review — verified within 24 hours"}
              </p>
            </div>
            <CaretDown
              size={14}
              className={`shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-150 ${manualOpen ? "rotate-180" : ""}`}
            />
          </button>

          {manualOpen && (
            <div className="px-4 pb-4">
              {!isQADCourse && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Scan with eSewa, Khalti, or any Fonepay-linked banking app, then upload
                    your receipt below.{" "}
                    <span
                      className="text-primary dark:text-primary-300 cursor-pointer underline font-semibold"
                      onClick={() => setVisible(true)}
                    >
                      Other payment options
                    </span>
                  </p>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 w-fit">
                    <Image
                      id={classRoom._id.$oid}
                      src="/img/fonepay-qr.jpg"
                      alt="Fonepay QR Code"
                      width={220}
                      height={204}
                    />
                  </div>
                </div>
              )}
              <RegisterUploadImage
                isFree={isQADCourse}
                refetch={refetch}
                registration={registration}
              />
            </div>
          )}
        </div>
      )}

      <Drawer
        title="Payment Methods"
        placement="right"
        closable={true}
        onClose={() => setVisible(false)}
        open={visible}
        width={320}
      >
        <ContentRenderer>{paymentDetails}</ContentRenderer>
      </Drawer>
    </div>
  );
}

export default PaymentMethods;
